import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { logEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { useTenantConfig } from "@/context/TenantConfigContext";
import { ActivityLoggerButton, ActivitiesList } from "@/components/ActivityLogger";
import { ReversePaymentButton } from "@/components/ReversePayment";
import { buildClonePrefill } from "@/components/QuickAddAccountDialog";
import { openRetainWhatsApp } from "@/lib/retainWhatsApp";
import { BucketBadge, StatusBadge, ChannelBadge, CHANNELS, CHANNEL_META } from "@/components/ui-bits";
import { DateField } from "@/components/DateField";
import { fmtINR, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, NotePencil, Calendar, CurrencyInr, Handshake, ChatCircleDots, UserSwitch, Flag,
  PhoneCall, CreditCard, ClockCounterClockwise, User, Bank, CalendarX, Brain, FilePdf, WhatsappLogo, EnvelopeSimple, ChatText, Eye, Gavel, SealCheck, Lock, PencilSimple, AddressBook, Plus, Trash, PaperPlaneTilt, Copy,
} from "@phosphor-icons/react";

const TYPE_ICON = { note: PhoneCall, ptp: Flag, payment: CreditCard, comm: ChatCircleDots, settlement: Handshake, status: ClockCounterClockwise, task: Calendar };
const PROBS = ["High", "Medium", "Low"];
const CLASSES = ["Likely To Pay", "Settlement Candidate", "Skip Trace Required", "Legal Candidate"];
const PROB_COLOR = { High: "bg-emerald-100 text-emerald-800", Medium: "bg-amber-100 text-amber-800", Low: "bg-red-100 text-red-800" };
const CLASS_COLOR = { "Likely To Pay": "bg-emerald-100 text-emerald-800", "Settlement Candidate": "bg-blue-100 text-blue-800", "Skip Trace Required": "bg-amber-100 text-amber-800", "Legal Candidate": "bg-red-100 text-red-800" };

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useTenantConfig();
  const [acc, setAcc] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [comms, setComms] = useState([]);
  const [payments, setPayments] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [summary, setSummary] = useState({});
  const [activities, setActivities] = useState([]);
  const [agents, setAgents] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [pinReq, setPinReq] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const canManage = ["super_admin", "client_admin", "manager"].includes(user.role);
  const isAuditor = user.role === "auditor";
  const isSuperAdmin = user.role === "super_admin";
  const isReadOnly = false; // Super Admin now has full Client-Admin capabilities on accounts
  const isClientAdmin = user.role === "client_admin";

  // Client Admin changes require a confirmation PIN; Super Admin (top authority) and others run directly.
  const confirmWithPin = (fn) => {
    if (isClientAdmin) setPinReq({ run: fn });
    else fn(null);
  };

  const load = useCallback(async () => {
    const [a, t, c, p, s, d, sm, ac] = await Promise.all([
      api.get(`/accounts/${id}`),
      api.get(`/accounts/${id}/timeline`),
      api.get(`/communications`, { params: { account_id: id } }),
      api.get(`/payments`, { params: { account_id: id } }),
      api.get(`/settlements`),
      api.get(`/documents`, { params: { account_id: id } }),
      api.get(`/accounts/${id}/comm-summary`),
      api.get(`/accounts/${id}/activities`),
    ]);
    setAcc(a.data);
    setTimeline(t.data);
    setComms(c.data);
    setPayments(p.data);
    setSettlements(s.data.filter((x) => x.account_id === id));
    setDocuments(d.data);
    setSummary(sm.data);
    setActivities(ac.data);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (canManage && acc?.tenant_id) {
      const params = isSuperAdmin ? { tenant_id: acc.tenant_id } : {};
      api.get("/agents", { params }).then((r) => setAgents(r.data)).catch(() => {});
    }
  }, [canManage, isSuperAdmin, acc?.tenant_id]);

  const saveIntel = (field, value) => {
    confirmWithPin(async (pin) => {
      try {
        await api.patch(`/accounts/${id}`, { [field]: value, ...(pin ? { pin } : {}) });
        setAcc((p) => ({ ...p, [field]: value }));
        toast.success("Updated");
      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    });
  };

  const saveEdit = (fields) => {
    confirmWithPin(async (pin) => {
      try {
        const r = await api.patch(`/accounts/${id}`, { ...fields, ...(pin ? { pin } : {}) });
        setAcc(r.data);
        setEditOpen(false);
        toast.success("Account updated");
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    });
  };

  const runAiScore = async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/accounts/${id}/ai-score`);
      setAcc((p) => ({ ...p, ...r.data }));
      toast.success(`AI score: ${r.data.ai_score} (${r.data.ai_band})`);
    } catch (e) { toast.error(e.response?.data?.detail || "AI scoring failed"); }
    finally { setAiLoading(false); }
  };

  if (!acc) return <div className="label-caps">Loading…</div>;

  const actions = [
    !isAuditor && !isReadOnly && { key: "note", label: "Log Call", icon: PhoneCall },
    !isAuditor && !isReadOnly && { key: "ptp", label: "Add PTP", icon: Flag },
    !isAuditor && !isReadOnly && { key: "followup", label: "Follow-up", icon: Calendar },
    !isAuditor && !isReadOnly && { key: "payment", label: "Payment", icon: CurrencyInr },
    !isAuditor && !isReadOnly && { key: "comm", label: "Send Msg", icon: ChatCircleDots },
    !isAuditor && !isReadOnly && { key: "settlement", label: "Settlement", icon: Handshake },
    canManage && !isReadOnly && { key: "assign", label: "Assign", icon: UserSwitch },
    !isAuditor && !isReadOnly && !["settled", "closed"].includes(acc.status) && !(acc.status_locked_by_admin && !(isClientAdmin || isSuperAdmin)) && { key: "closeAccount", label: "Settle / Close", icon: SealCheck },
    (isClientAdmin || isSuperAdmin) && { key: "setStatus", label: "Set / Override Status", icon: Lock },
  ].filter(Boolean);

  const editBtn = (isClientAdmin || isSuperAdmin) ? (
    <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => setEditOpen(true)} data-testid="edit-account-btn"><PencilSimple size={14} weight="bold" /> Edit Account</Button>
  ) : null;

  const whatsappRetainBtn = canManage ? (
    <Button
      size="sm"
      className="rounded-sm gap-1.5 bg-[#25D366] hover:bg-[#1da851] text-white"
      onClick={() => openRetainWhatsApp(acc, toast)}
      data-testid="whatsapp-retain-btn"
    >
      <WhatsappLogo size={15} weight="fill" /> WhatsApp
    </Button>
  ) : null;

  const cloneBtn = canManage ? (
    <Button
      size="sm"
      variant="outline"
      className="rounded-sm gap-1.5"
      onClick={() => window.dispatchEvent(new CustomEvent("recoveros:quick-add", { detail: { source: "clone_detail", prefill: buildClonePrefill(acc) } }))}
      data-testid="clone-account-btn"
    >
      <Copy size={14} weight="bold" /> Clone Account
    </Button>
  ) : null;

  const startFollowupBtn = (isClientAdmin || isSuperAdmin) ? (
    <Button
      size="sm"
      className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]"
      onClick={() => { logEvent("start_followup_clicked", { source: "account_detail", account_id: id }); navigate(`/connect?account=${id}&compose=1`); }}
      data-testid="account-start-followup-btn"
    >
      <PaperPlaneTilt size={15} weight="bold" /> Start a Follow-up
    </Button>
  ) : null;

  const wa = comms.filter((c) => c.channel === "whatsapp");
  const sms = comms.filter((c) => c.channel === "sms");
  const email = comms.filter((c) => c.channel === "email");
  const notes = timeline.filter((e) => e.type === "note");

  return (
    <div data-testid="account-detail-page">
      <button onClick={() => navigate(isReadOnly ? "/tenants" : "/accounts")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
        <ArrowLeft size={16} /> {isReadOnly ? "Back to tenants" : "Back to accounts"}
      </button>

      {/* Header */}
      <div className="border border-border bg-card p-6 mb-4">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div>
            <div className="label-caps">{acc.record_id ? `${acc.record_id} • ` : ""}{acc.customer_id} • {acc.loan_id} • {acc.product || "Loan"}</div>
            <h1 className="text-2xl font-bold mt-1">{acc.borrower_name}</h1>
            <div className="text-sm text-muted-foreground mono mt-1">{acc.mobile} • {acc.email}</div>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2">
            <div className="flex flex-wrap items-center gap-2">{startFollowupBtn}{whatsappRetainBtn}</div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={acc.status} />
              {acc.status_locked_by_admin && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-sm bg-foreground text-background" title={`Status locked by Client Admin${acc.status_set_by ? ` (${acc.status_set_by})` : ""}`} data-testid="status-locked-badge">
                  <Lock size={12} weight="bold" /> Admin-locked
                </span>
              )}
              <BucketBadge bucket={acc.bucket} />
              {acc.account_classification && <span className={`px-2 py-0.5 text-xs font-semibold rounded-sm ${CLASS_COLOR[acc.account_classification] || "bg-secondary"}`}>{acc.account_classification}</span>}
              {acc.recovery_probability && <span className={`px-2 py-0.5 text-xs font-semibold rounded-sm ${PROB_COLOR[acc.recovery_probability] || "bg-secondary"}`}>{acc.recovery_probability} Prob.</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-5 pt-4 border-t border-border">
          <Field label="Outstanding" value={<span className="text-destructive">{fmtINR(acc.outstanding_amount)}</span>} />
          <Field label="DPD" value={acc.dpd == null ? "Unknown" : `${acc.dpd} days`} />
          <Field label="Assigned Agent" value={(acc.assigned_agent_names && acc.assigned_agent_names.length > 0) ? acc.assigned_agent_names.join(", ") : (acc.assigned_agent_name || "Unassigned")} />
          <div data-testid="last-contact-date"><div className="label-caps">Last Contact</div><div className="text-sm font-semibold mt-0.5 mono">{summary.last_contact ? fmtDate(summary.last_contact) : "—"}</div></div>
          <div data-testid="next-followup-date"><div className="label-caps">Next Follow-Up</div><div className="text-sm font-semibold mt-0.5 mono">{summary.next_followup ? fmtDate(summary.next_followup) : "—"}</div></div>
        </div>
      </div>

      {/* Quick actions */}
      {actions.length > 0 && (
      <div className="border border-border bg-card p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-caps">Quick Actions</div>
          {!isAuditor && !isReadOnly && <ActivityLoggerButton accountId={id} onSaved={load} outstanding={acc.outstanding_amount} />}
        </div>
        <div className="flex flex-wrap gap-2">
          {actions.map((a) => (
            <Button key={a.key} variant="outline" size="sm" className="rounded-sm gap-2 h-9" onClick={() => setDlg(a.key)} data-testid={`action-${a.key}`}>
              <a.icon size={15} weight="bold" /> {a.label}
            </Button>
          ))}
          {editBtn}
          {cloneBtn}
        </div>
      </div>
      )}

      {/* Communication summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <CommStat icon={PhoneCall} label="Last Call" ts={summary.last_call} color="text-orange-600" />
        <CommStat icon={WhatsappLogo} label="Last WhatsApp" ts={summary.last_whatsapp} color="text-emerald-600" />
        <CommStat icon={ChatText} label="Last SMS" ts={summary.last_sms} color="text-blue-600" />
        <CommStat icon={EnvelopeSimple} label="Last Email" ts={summary.last_email} color="text-violet-600" />
      </div>

      {/* Info cards */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <InfoCard icon={User} title="Customer Profile" rows={[
          ["Customer ID", acc.customer_id], ["Name", acc.borrower_name], ["Mobile", acc.mobile],
          ["Alternate Mobile", acc.alternate_mobile], ["Email", acc.email],
          ["City", acc.city], ["State", acc.state], ["Pincode", acc.pincode],
        ]} />
        <InfoCard icon={Bank} title="Loan Information" rows={[
          ["Record ID", acc.record_id || "—"],
          ["Loan ID", acc.loan_id], ["Product", acc.product], ["Loan Amount", fmtINR(acc.loan_amount)],
          ["Receivable", fmtINR(acc.receivable_amount > 0 ? acc.receivable_amount : acc.loan_amount)],
          ["Disbursal Date", fmtDate(acc.disbursal_date)], ["Tenure", acc.loan_tenure], ["EMI", fmtINR(acc.emi_amount)],
          ["Outstanding", fmtINR(acc.outstanding_amount)], ["Principal Os", fmtINR(acc.principal_outstanding)], ["Interest Os", fmtINR(acc.interest_outstanding)],
        ]} />
        <InfoCard icon={CalendarX} title="DPD & Collection" rows={[
          ["DPD", acc.dpd == null ? "Unknown" : `${acc.dpd} days`], ["Bucket", acc.bucket], ["Payment Due Date", fmtDate(acc.emi_due_date)],
          ["Last Payment Date", fmtDate(acc.last_payment_date)], ["Last Payment Amount", fmtINR(acc.last_payment_amount)],
          ["Total Overdue", fmtINR(acc.total_overdue_amount)], ["Assigned Agent", acc.assigned_agent_name || "Unassigned"],
          ["Assigned Agency", acc.assigned_agency], ["Collection Manager", acc.collection_manager],
        ]} />

        {/* Recovery status + intelligence */}
        <div className="border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Brain size={18} weight="bold" className="text-primary" />
            <h3 className="font-heading font-bold text-lg">Recovery Status & Intelligence</h3>
          </div>
          <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm mb-4">
            <Field label="Recovery Amount" value={fmtINR(acc.recovery_amount)} />
            <Field label="Recovery Date" value={fmtDate(acc.recovery_date)} />
            <Field label="Recovery Status" value={<span className="capitalize">{acc.recovery_status || "pending"}</span>} />
            <Field label="Settlement Status" value={acc.settlement_status ? <StatusBadge status={acc.settlement_status} /> : "—"} />
          </div>
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
            <div>
              <Label className="text-xs">Recovery Probability</Label>
              <Select value={acc.recovery_probability || ""} onValueChange={(v) => saveIntel("recovery_probability", v)} disabled={isAuditor || isReadOnly}>
                <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="recovery-probability-select"><SelectValue placeholder="Set" /></SelectTrigger>
                <SelectContent>{PROBS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Account Classification</Label>
              <Select value={acc.account_classification || ""} onValueChange={(v) => saveIntel("account_classification", v)} disabled={isAuditor || isReadOnly}>
                <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="account-classification-select"><SelectValue placeholder="Set" /></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {!isReadOnly && (
            <div className="pt-3 mt-3 border-t border-border" data-testid="ai-score-block">
              {acc.ai_score != null ? (
                <div className="flex items-start gap-3">
                  <div className={`flex flex-col items-center justify-center h-14 w-14 rounded-sm border shrink-0 ${acc.ai_band === "High" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : acc.ai_band === "Medium" ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-red-50 border-red-300 text-red-700"}`}>
                    <span className="text-lg font-black font-heading mono leading-none">{acc.ai_score}</span>
                    <span className="text-[9px] font-bold uppercase tracking-wide mt-0.5">{acc.ai_band}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5"><Brain size={14} weight="bold" className="text-primary" /><span className="label-caps">AI Likelihood to Pay</span></div>
                    <p className="text-sm mt-1">{acc.ai_reason}</p>
                    {acc.ai_action && <p className="text-xs text-primary font-medium mt-1">Next: {acc.ai_action}</p>}
                    {!isAuditor && <button onClick={runAiScore} disabled={aiLoading} className="text-xs text-muted-foreground hover:text-foreground mt-1.5 underline" data-testid="ai-rescore-btn">{aiLoading ? "Scoring…" : "Re-score"}</button>}
                  </div>
                </div>
              ) : (
                !isAuditor && (
                  <Button variant="outline" size="sm" className="rounded-sm gap-2 w-full" onClick={runAiScore} disabled={aiLoading} data-testid="ai-score-btn">
                    <Brain size={15} weight="bold" /> {aiLoading ? "Analyzing…" : "Run AI Recovery Score"}
                  </Button>
                )
              )}
            </div>
          )}
        </div>
      </div>

      <CustomFieldsCard acc={acc} fields={config.custom_fields} title={config.name} confirmWithPin={confirmWithPin} canEdit={!isAuditor && !isReadOnly} onChanged={load} />

      <ReferenceContacts acc={acc} confirmWithPin={confirmWithPin} isAuditor={isAuditor} isReadOnly={isReadOnly} onChanged={load} />
      {(acc.legal_status || acc.court_case_no || acc.cheque_bounced || acc.next_hearing_date) && (
        <div className="border border-rose-200 bg-rose-50/40 p-5 mb-4" data-testid="legal-section">
          <div className="flex items-center gap-2 mb-3">
            <Gavel size={18} weight="bold" className="text-rose-700" />
            <h3 className="font-heading font-bold text-lg text-rose-900">Legal Case</h3>
            {acc.cheque_bounced && <span className="px-2 py-0.5 text-[11px] font-semibold rounded-sm bg-red-200 text-red-900">Cheque Bounced · Sec 138</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Legal Status" value={acc.legal_status || "—"} />
            <Field label="Court Case No." value={acc.court_case_no || "—"} />
            <Field label="Next Hearing" value={acc.next_hearing_date ? fmtDate(acc.next_hearing_date) : "—"} />
            <Field label="Advocate" value={acc.advocate || "—"} />
          </div>
        </div>
      )}

      <div className="border border-border bg-card p-5">
        <h3 className="font-heading font-bold text-lg mb-4">Account History</h3>
        <Tabs defaultValue="timeline">
          <TabsList className="rounded-sm flex-wrap h-auto">
            <TabsTrigger value="timeline" className="rounded-sm" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="activities" className="rounded-sm" data-testid="tab-activities">Activity Log ({activities.length})</TabsTrigger>
            <TabsTrigger value="calls" className="rounded-sm">Calls & Notes ({notes.length})</TabsTrigger>
            <TabsTrigger value="whatsapp" className="rounded-sm">WhatsApp ({wa.length})</TabsTrigger>
            <TabsTrigger value="sms" className="rounded-sm">SMS ({sms.length})</TabsTrigger>
            <TabsTrigger value="email" className="rounded-sm">Email ({email.length})</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-sm">Payments ({payments.length})</TabsTrigger>
            <TabsTrigger value="settlements" className="rounded-sm">Settlements ({settlements.length})</TabsTrigger>
            <TabsTrigger value="documents" className="rounded-sm" data-testid="tab-documents">Documents ({documents.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-4"><Timeline events={timeline} /></TabsContent>
          <TabsContent value="activities" className="mt-4"><ActivitiesList items={activities} /></TabsContent>
          <TabsContent value="calls" className="mt-4"><SimpleList items={notes.map((n) => ({ title: n.title, detail: n.detail, ts: n.ts, by: n.by }))} empty="No calls or notes" /></TabsContent>
          <TabsContent value="whatsapp" className="mt-4"><CommList items={wa} /></TabsContent>
          <TabsContent value="sms" className="mt-4"><CommList items={sms} /></TabsContent>
          <TabsContent value="email" className="mt-4"><CommList items={email} /></TabsContent>
          <TabsContent value="payments" className="mt-4">
            {payments.length === 0 ? (
              <SimpleList items={[]} empty="No payments" />
            ) : (
              <div className="space-y-2" data-testid="payments-tab-list">
                {payments.map((p) => (
                  <div key={p.id} className="border border-border p-3 flex items-center justify-between gap-3" data-testid={`payment-row-${p.id}`}>
                    <div>
                      <div className="font-medium">{fmtINR(p.amount)} • {p.mode}</div>
                      <div className="text-xs text-muted-foreground">Ref: {p.reference || "—"} · {fmtDateTime(p.created_at)} · {p.recorded_by}</div>
                    </div>
                    {!isAuditor && !isReadOnly && <ReversePaymentButton payment={p} onReversed={load} variant="button" />}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="settlements" className="mt-4">
            <div className="space-y-2">
              {settlements.length === 0 && <Empty text="No settlements" />}
              {settlements.map((s) => (
                <div key={s.id} className="flex items-center justify-between border border-border p-3">
                  <div>
                    <div className="font-medium">{fmtINR(s.settlement_amount)} <span className="text-xs text-muted-foreground">vs {fmtINR(s.outstanding_amount)} outstanding</span></div>
                    <div className="text-xs text-muted-foreground">{s.reason}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={s.status} />
                    <a href={`${API}/settlements/${s.id}/pdf?doc=letter`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="rounded-sm gap-1"><FilePdf size={14} /> PDF</Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <div className="space-y-2">
              {documents.length === 0 && <Empty text="No documents generated yet" />}
              {documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-border p-3">
                  <div className="flex items-center gap-3">
                    <FilePdf size={20} weight="bold" className="text-destructive" />
                    <div>
                      <div className="font-medium text-sm">{d.title}</div>
                      <div className="label-caps">{(d.doc_type || "").replace(/_/g, " ")} • {fmtDateTime(d.created_at)}</div>
                    </div>
                  </div>
                  {d.settlement_id && (
                    <a href={`${API}/settlements/${d.settlement_id}/pdf?doc=${d.doc_type === "noc" ? "noc" : d.doc_type.includes("completion") ? "completion" : d.doc_type.includes("approval") ? "approval" : "letter"}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="rounded-sm">Open</Button>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ActionDialog dlg={dlg} setDlg={setDlg} acc={acc} agents={agents} onDone={load} confirmWithPin={confirmWithPin} isClientAdmin={isClientAdmin} />
      {editOpen && <EditAccountDialog acc={acc} onClose={() => setEditOpen(false)} onSave={saveEdit} />}
      {pinReq && <ConfirmPinDialog onClose={() => setPinReq(null)} onConfirm={(pin) => { const fn = pinReq.run; setPinReq(null); fn(pin); }} />}
    </div>
  );
}

function CommStat({ icon: Icon, label, ts, color }) {
  return (
    <div className="border border-border bg-card p-3 flex items-center gap-3">
      <Icon size={20} weight="bold" className={color} />
      <div>
        <div className="label-caps">{label}</div>
        <div className="text-sm font-semibold mono mt-0.5">{ts ? fmtDate(ts) : "—"}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, rows }) {
  return (
    <div className="border border-border bg-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} weight="bold" className="text-primary" />
        <h3 className="font-heading font-bold text-lg">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-y-3 gap-x-4">
        {rows.map(([l, v]) => <Field key={l} label={l} value={v || "—"} />)}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className="text-sm font-semibold mt-0.5 mono break-words">{value}</div>
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-muted-foreground text-center py-8">{text}</p>;
}

function Timeline({ events }) {
  if (!events.length) return <Empty text="No activity yet" />;
  return (
    <div className="space-y-0">
      {events.map((ev, i) => {
        const Icon = TYPE_ICON[ev.type] || NotePencil;
        return (
          <div key={`${ev.type}-${ev.ts}-${i}`} className="flex gap-3 pb-5 last:pb-0">
            <div className="flex flex-col items-center">
              <div className="h-8 w-8 rounded-sm bg-secondary border border-border flex items-center justify-center shrink-0">
                <Icon size={15} weight="bold" className="text-primary" />
              </div>
              {i < events.length - 1 && <div className="w-px flex-1 bg-border my-1" />}
            </div>
            <div className="flex-1 pt-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">{ev.title}</span>
                <span className="text-xs text-muted-foreground mono shrink-0">{fmtDateTime(ev.ts)}</span>
              </div>
              {ev.detail && <p className="text-sm text-muted-foreground mt-0.5">{ev.detail}</p>}
              {ev.by && <span className="label-caps">{ev.by}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SimpleList({ items, empty }) {
  if (!items.length) return <Empty text={empty} />;
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={`${it.ts}-${it.title}-${i}`} className="border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold">{it.title}</span>
            <span className="text-xs text-muted-foreground mono shrink-0">{fmtDateTime(it.ts)}</span>
          </div>
          {it.detail && <p className="text-sm text-muted-foreground mt-0.5">{it.detail}</p>}
          {it.by && <span className="label-caps">{it.by}</span>}
        </div>
      ))}
    </div>
  );
}

function CommList({ items }) {
  if (!items.length) return <Empty text="No messages on this channel" />;
  return (
    <div className="space-y-2">
      {items.map((c) => (
        <div key={c.id} className="border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <ChannelBadge channel={c.channel} />
            <div className="flex items-center gap-2">
              <StatusBadge status={c.status} />
              <span className="text-xs text-muted-foreground mono">{fmtDateTime(c.created_at)}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-2">{c.body}</p>
          <span className="label-caps">To: {c.to}</span>
        </div>
      ))}
    </div>
  );
}

function ActionDialog({ dlg, setDlg, acc, agents, onDone, confirmWithPin, isClientAdmin }) {
  const [form, setForm] = useState({});
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  const close = () => { setDlg(null); setForm({}); };

  const submit = async () => {
    const id = acc.account_id;
    // Status override by Client Admin is PIN-gated
    if (dlg === "setStatus") {
      confirmWithPin(async (pin) => {
        try {
          await api.post(`/accounts/${id}/status`, { status: form.status, note: form.remarks || "", ...(pin ? { pin } : {}) });
          toast.success("Status updated & locked");
          close(); onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
      });
      return;
    }
    try {
      if (dlg === "note") await api.post(`/accounts/${id}/notes`, { note: form.note, disposition: form.disposition || "Called" });
      else if (dlg === "ptp") await api.post(`/accounts/${id}/ptp`, { amount: +form.amount || 0, promise_date: form.promise_date, remarks: form.remarks });
      else if (dlg === "followup") { await api.post(`/accounts/${id}/followup`, { due_date: form.due_date, note: form.note }); logEvent("followup_created", { source: "account_detail", account_id: id }); }
      else if (dlg === "payment") await api.post(`/payments`, { account_id: id, amount: +form.amount || 0, mode: form.mode || "UPI", reference: form.reference, payment_date: form.payment_date || undefined });
      else if (dlg === "comm") await api.post(`/communications/send`, { account_id: id, channel: form.channel || "whatsapp", body: form.body, scheduled_at: form.scheduled_at || "" });
      else if (dlg === "settlement") await api.post(`/settlements`, { account_id: id, settlement_amount: +form.amount || 0, reason: form.reason, notes: form.note });
      else if (dlg === "assign") await api.post(`/accounts/${id}/assign`, { agent_ids: form.agent_ids ?? (acc.assigned_agent_ids || []) });
      else if (dlg === "closeAccount") await api.post(`/accounts/${id}/activities`, { activity_type: "Account Update", outcome: form.outcome || "Settled", remarks: form.remarks || "" });
      toast.success("Done");
      close();
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const titles = { note: "Log Call / Note", ptp: "Add Promise to Pay", followup: "Schedule Follow-up", payment: "Record Payment", comm: "Send Communication", settlement: "Raise Settlement Request", assign: "Assign Agent", closeAccount: "Settle / Close Account", setStatus: "Set / Override Account Status" };

  return (
    <Dialog open={!!dlg} onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>{titles[dlg]}</DialogTitle>
          <DialogDescription>Record this activity against the borrower account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {dlg === "note" && <>
            <div><Label className="text-xs">Disposition</Label>
              <Select value={form.disposition || "Called"} onValueChange={(v) => setForm({ ...form, disposition: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["Called", "Not Reachable", "PTP", "Dispute", "Wrong Number"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Note</Label><Textarea className="rounded-sm" value={form.note || ""} onChange={set("note")} data-testid="note-text" /></div>
          </>}
          {dlg === "ptp" && <>
            <div><Label className="text-xs">Amount</Label><Input type="number" className="rounded-sm" value={form.amount || ""} onChange={set("amount")} data-testid="ptp-amount" /></div>
            <div><Label className="text-xs">Promise Date</Label><Input type="date" className="rounded-sm" value={form.promise_date || ""} onChange={set("promise_date")} data-testid="ptp-date" /></div>
            <div><Label className="text-xs">Remarks</Label><Input className="rounded-sm" value={form.remarks || ""} onChange={set("remarks")} data-testid="ptp-remarks" /></div>
          </>}
          {dlg === "followup" && <>
            <div><Label className="text-xs">Due Date</Label><Input type="date" className="rounded-sm" value={form.due_date || ""} onChange={set("due_date")} data-testid="followup-date" /></div>
            <div><Label className="text-xs">Note</Label><Textarea className="rounded-sm" value={form.note || ""} onChange={set("note")} /></div>
          </>}
          {dlg === "payment" && <>
            <div><Label className="text-xs">Amount</Label><Input type="number" className="rounded-sm" value={form.amount || ""} onChange={set("amount")} data-testid="payment-amount" /></div>
            <div>
              <Label className="text-xs">Payment Date</Label>
              <Input type="date" className="rounded-sm" max={new Date().toISOString().slice(0, 10)} value={form.payment_date || new Date().toISOString().slice(0, 10)} onChange={set("payment_date")} data-testid="payment-date" />
              <p className="text-[11px] text-muted-foreground mt-1">Pick a past date to record a payment received earlier.</p>
            </div>
            <div><Label className="text-xs">Mode</Label>
              <Select value={form.mode || "UPI"} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["UPI", "NEFT", "Cash", "Cheque", "Card"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Reference</Label><Input className="rounded-sm" value={form.reference || ""} onChange={set("reference")} /></div>
          </>}
          {dlg === "comm" && <>
            <div><Label className="text-xs">Channel</Label>
              <Select value={form.channel || "whatsapp"} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger className="rounded-sm" data-testid="comm-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{CHANNEL_META[c].label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Message</Label><Textarea className="rounded-sm" value={form.body || ""} onChange={set("body")} data-testid="comm-body" /></div>
            {form.channel === "ivr" && (
              <div><Label className="text-xs">Schedule IVR (optional — leave blank for instant)</Label>
                <Input type="datetime-local" className="rounded-sm" value={form.scheduled_at || ""} onChange={set("scheduled_at")} data-testid="ivr-schedule" /></div>
            )}
            <p className="text-xs text-muted-foreground">Communication frequency and compliance caps apply.</p>
          </>}
          {dlg === "settlement" && <>
            <div><Label className="text-xs">Settlement Amount</Label><Input type="number" className="rounded-sm" value={form.amount || ""} onChange={set("amount")} data-testid="settlement-amount" /></div>
            <div><Label className="text-xs">Reason</Label><Input className="rounded-sm" value={form.reason || ""} onChange={set("reason")} /></div>
            <div><Label className="text-xs">Notes</Label><Textarea className="rounded-sm" value={form.note || ""} onChange={set("note")} /></div>
          </>}
          {dlg === "assign" && <>
            <div><Label className="text-xs">Assign Agents (one or many)</Label>
              <div className="border border-border rounded-sm divide-y divide-border max-h-56 overflow-y-auto mt-1">
                {agents.length === 0 && <div className="p-3 text-sm text-muted-foreground">No agents available</div>}
                {agents.map((a) => {
                  const picked = form.agent_ids ?? (acc.assigned_agent_ids || []);
                  const on = picked.includes(a.user_id);
                  return (
                    <label key={a.user_id} className="flex items-center gap-3 p-2.5 hover:bg-secondary/50 cursor-pointer" data-testid={`assign-agent-${a.user_id}`}>
                      <Checkbox checked={on} onCheckedChange={() => {
                        const next = on ? picked.filter((x) => x !== a.user_id) : [...picked, a.user_id];
                        setForm({ ...form, agent_ids: next });
                      }} />
                      <span className="text-sm font-medium">{a.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </>}
          {dlg === "closeAccount" && <>
            <div><Label className="text-xs">Action</Label>
              <Select value={form.outcome || "Settled"} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                <SelectTrigger className="rounded-sm" data-testid="close-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Settled", "Closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="border border-teal-200 bg-teal-50/60 p-3 rounded-sm text-xs text-teal-800" data-testid="close-account-note">
              This sets the account status to <b>{(form.outcome || "Settled") === "Settled" ? "settled" : "closed"}</b> and <b>clears its outstanding balance to ₹0</b>, removing it from Total Outstanding on the dashboard.
            </div>
            <div><Label className="text-xs">Remarks (optional)</Label><Textarea className="rounded-sm" value={form.remarks || ""} onChange={set("remarks")} data-testid="close-remarks" /></div>
          </>}
          {dlg === "setStatus" && <>
            <div><Label className="text-xs">Account Status</Label>
              <Select value={form.status || acc.status || ""} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm" data-testid="set-status-select"><SelectValue placeholder="Choose status" /></SelectTrigger>
                <SelectContent>{["active", "ptp", "settled", "closed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="border border-foreground/20 bg-secondary p-3 rounded-sm text-xs flex items-start gap-2" data-testid="set-status-note">
              <Lock size={14} weight="bold" className="mt-0.5 shrink-0" />
              <span>This <b>overrides and locks</b> the status. Agents and Managers will no longer be able to change it (only a Client Admin or Super Admin can).</span>
            </div>
            <div><Label className="text-xs">Reason / Note (optional)</Label><Textarea className="rounded-sm" value={form.remarks || ""} onChange={set("remarks")} data-testid="set-status-note-input" /></div>
          </>}
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm" data-testid="action-submit">Submit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EDIT_FIELD_GROUPS = [
  {
    group: "Customer",
    fields: [
      { k: "borrower_name", label: "Borrower Name" },
      { k: "mobile", label: "Mobile" },
      { k: "alternate_mobile", label: "Alternate Mobile" },
      { k: "email", label: "Email" },
      { k: "city", label: "City" },
      { k: "state", label: "State" },
      { k: "pincode", label: "Pincode" },
    ],
  },
  {
    group: "Loan",
    fields: [
      { k: "record_id", label: "Record ID" },
      { k: "loan_id", label: "Loan ID" },
      { k: "customer_id", label: "Customer ID" },
      { k: "loan_amount", label: "Loan Amount", num: true },
      { k: "receivable_amount", label: "Receivable Amount", num: true },
      { k: "emi_amount", label: "EMI Amount", num: true },
      { k: "outstanding_amount", label: "Outstanding Amount", num: true },
      { k: "disbursal_date", label: "Disbursal Date", date: true },
    ],
  },
  {
    group: "Payment",
    fields: [
      { k: "emi_due_date", label: "Payment Due Date", date: true },
      { k: "last_payment_date", label: "Last Payment Date", date: true },
      { k: "last_payment_amount", label: "Last Payment Amount", num: true },
    ],
  },
];
const EDIT_FIELDS = EDIT_FIELD_GROUPS.flatMap((g) => g.fields);

function EditAccountDialog({ acc, onClose, onSave }) {
  const [f, setF] = useState(() => Object.fromEntries(EDIT_FIELDS.map((x) => [x.k, acc[x.k] ?? ""])));
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = () => {
    const payload = {};
    EDIT_FIELDS.forEach((x) => { payload[x.k] = x.num ? (+f[x.k] || 0) : f[x.k]; });
    onSave(payload);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto" data-testid="edit-account-dialog">
        <DialogHeader>
          <DialogTitle>Edit Account — {acc.borrower_name}</DialogTitle>
          <DialogDescription>Edit account fields — you'll confirm with your PIN. DPD is auto-calculated from the Payment Due Date (not editable directly).</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {EDIT_FIELD_GROUPS.map((g) => (
            <div key={g.group} data-testid={`edit-group-${g.group.toLowerCase()}`}>
              <div className="label-caps text-muted-foreground mb-2 pb-1 border-b border-border">{g.group}</div>
              <div className="grid grid-cols-2 gap-3">
                {g.fields.map((x) => (
                  <div key={x.k} className={x.k === "email" ? "col-span-2" : ""}>
                    <Label className="text-xs">{x.label}</Label>
                    {x.date
                      ? <DateField value={f[x.k]} onChange={(v) => upd(x.k, v)} testId={`edit-${x.k}`} />
                      : <Input type={x.num ? "number" : "text"} className="rounded-sm" value={f[x.k]} onChange={(e) => upd(x.k, e.target.value)} data-testid={`edit-${x.k}`} />}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm" data-testid="edit-account-save">Save Changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmPinDialog({ onClose, onConfirm }) {
  const [pin, setPin] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const doReset = async () => {
    try {
      await api.post(`/me/pin/reset`, { current_pin: cur, new_pin: nw });
      toast.success("PIN updated");
      setResetOpen(false); setCur(""); setNw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reset PIN"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="confirm-pin-dialog">
        <DialogHeader>
          <DialogTitle>Do you want to make the changes?</DialogTitle>
          <DialogDescription>Enter your 4-digit Client Admin PIN to confirm. (Default PIN is 4220.)</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Confirmation PIN</Label>
            <Input type="password" inputMode="numeric" maxLength={4} className="rounded-sm tracking-[0.5em] text-center text-lg" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="confirm-pin-input" autoFocus />
          </div>
          <button onClick={() => setResetOpen((v) => !v)} className="text-xs text-primary underline" data-testid="pin-reset-toggle">Reset PIN</button>
          {resetOpen && (
            <div className="border border-border rounded-sm p-3 space-y-2 bg-secondary/40">
              <div><Label className="text-xs">Current PIN</Label><Input type="password" maxLength={4} className="rounded-sm" value={cur} onChange={(e) => setCur(e.target.value.replace(/\D/g, ""))} data-testid="pin-current" /></div>
              <div><Label className="text-xs">New PIN (4 digits)</Label><Input type="password" maxLength={4} className="rounded-sm" value={nw} onChange={(e) => setNw(e.target.value.replace(/\D/g, ""))} data-testid="pin-new" /></div>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={doReset} data-testid="pin-reset-save">Update PIN</Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button className="rounded-sm" disabled={pin.length !== 4} onClick={() => onConfirm(pin)} data-testid="confirm-pin-submit">Confirm Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReferenceContacts({ acc, confirmWithPin, isAuditor, isReadOnly, onChanged }) {
  const [contacts, setContacts] = useState(acc.reference_contacts || []);
  const [rels, setRels] = useState([]);
  const [max, setMax] = useState(10);
  const [editing, setEditing] = useState(null); // {id?, name, relationship, phone}
  const canEdit = !isAuditor && !isReadOnly;

  useEffect(() => { setContacts(acc.reference_contacts || []); }, [acc.reference_contacts]);
  useEffect(() => { api.get("/reference-options").then((r) => { setRels(r.data.relationships || []); setMax(r.data.max || 10); }).catch(() => {}); }, []);

  const save = (data) => {
    confirmWithPin(async (pin) => {
      try {
        const body = { ...data, ...(pin ? { pin } : {}) };
        const res = editing?.id
          ? await api.patch(`/accounts/${acc.account_id}/reference-contacts/${editing.id}`, body)
          : await api.post(`/accounts/${acc.account_id}/reference-contacts`, body);
        setContacts(res.data); setEditing(null);
        toast.success("Reference contact saved");
        onChanged && onChanged();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to save contact"); }
    });
  };

  const remove = (c) => {
    confirmWithPin(async (pin) => {
      try {
        const res = await api.delete(`/accounts/${acc.account_id}/reference-contacts/${c.id}`, { params: pin ? { pin } : {} });
        setContacts(res.data);
        toast.success("Reference contact removed");
        onChanged && onChanged();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove"); }
    });
  };

  return (
    <div className="border border-border bg-card p-5 mb-4" data-testid="reference-contacts-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AddressBook size={18} weight="bold" className="text-primary" />
          <h3 className="font-heading font-bold text-lg">Reference Contacts</h3>
          <span className="text-xs text-muted-foreground mono">({contacts.length}/{max})</span>
        </div>
        {canEdit && contacts.length < max && (
          <Button size="sm" variant="outline" className="rounded-sm gap-2 h-9" onClick={() => setEditing({ name: "", relationship: "", phone: "" })} data-testid="add-reference-btn">
            <Plus size={15} weight="bold" /> Add Contact
          </Button>
        )}
      </div>

      {contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No reference contacts added. Capture up to {max} alternate numbers (family, friends, references).</p>
      ) : (
        <div className="divide-y divide-border border border-border rounded-sm">
          {contacts.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-3" data-testid={`reference-row-${c.id}`}>
              <span className="px-2 py-0.5 text-[11px] font-semibold rounded-sm bg-secondary shrink-0">{c.relationship}</span>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{c.name}</div>
                <a href={`tel:${c.phone}`} className="text-sm text-primary mono hover:underline" data-testid={`reference-phone-${c.id}`}>{c.phone}</a>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {canEdit && (
                  <ActivityLoggerButton accountId={acc.account_id} variant="icon" testIdSuffix={`ref-${c.id}`} borrowerName={acc.borrower_name} presetContact={c} presetType="Call" onSaved={onChanged} />
                )}
                {canEdit && (
                  <>
                    <button onClick={() => setEditing(c)} title="Edit" className="h-8 w-8 inline-flex items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary" data-testid={`reference-edit-${c.id}`}><PencilSimple size={15} weight="bold" /></button>
                    <button onClick={() => remove(c)} title="Delete" className="h-8 w-8 inline-flex items-center justify-center rounded-sm text-red-600 hover:bg-red-50" data-testid={`reference-delete-${c.id}`}><Trash size={15} weight="bold" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ReferenceEditor editing={editing} rels={rels} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ReferenceEditor({ editing, rels, onClose, onSave }) {
  const [f, setF] = useState({ name: editing.name || "", relationship: editing.relationship || "", phone: editing.phone || "" });
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = () => {
    if (!f.name.trim() || !f.relationship || !f.phone.trim()) return toast.error("Name, relationship and phone are required");
    onSave(f);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="reference-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editing.id ? "Edit" : "Add"} Reference Contact</DialogTitle>
          <DialogDescription>Capture an alternate person to reach for this borrower.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Relationship</Label>
            <Select value={f.relationship || undefined} onValueChange={(v) => upd("relationship", v)}>
              <SelectTrigger className="rounded-sm" data-testid="reference-relationship-select"><SelectValue placeholder="Select relationship" /></SelectTrigger>
              <SelectContent>{rels.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Name</Label><Input className="rounded-sm" value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="reference-name-input" /></div>
          <div><Label className="text-xs">Phone Number</Label><Input className="rounded-sm" value={f.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="reference-phone-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button className="rounded-sm" onClick={submit} data-testid="reference-save-btn">Save Contact</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomFieldsCard({ acc, fields, title, confirmWithPin, canEdit, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({});
  if (!fields || fields.length === 0) return null;
  const cf = acc.custom_fields || {};

  const openEdit = () => {
    const init = {};
    fields.forEach((x) => { init[x.key] = cf[x.key] ?? ""; });
    setF(init);
    setEditing(true);
  };
  const save = () => {
    confirmWithPin(async (pin) => {
      try {
        await api.patch(`/accounts/${acc.account_id}`, { custom_fields: f, ...(pin ? { pin } : {}) });
        toast.success("Details updated");
        setEditing(false);
        onChanged && onChanged();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    });
  };

  return (
    <div className="border border-border bg-card p-5 mb-4" data-testid="custom-fields-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <SealCheck size={18} weight="bold" className="text-primary" />
          <h3 className="font-heading font-bold text-lg">{title} Details</h3>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" className="rounded-sm gap-2 h-9" onClick={openEdit} data-testid="edit-custom-fields-btn">
            <PencilSimple size={15} weight="bold" /> Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {fields.map((x) => (
          <div key={x.key} data-testid={`custom-field-${x.key}`}>
            <div className="label-caps">{x.label}</div>
            <div className="text-sm font-medium mt-1">{cf[x.key] !== undefined && cf[x.key] !== "" ? String(cf[x.key]) : "—"}</div>
          </div>
        ))}
      </div>

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(false)}>
          <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto" data-testid="custom-fields-dialog">
            <DialogHeader>
              <DialogTitle>Edit {title} Details</DialogTitle>
              <DialogDescription>Vertical-specific information for this {acc.borrower_name ? "record" : "account"}.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {fields.map((x) => (
                <div key={x.key}>
                  <Label className="text-xs">{x.label}</Label>
                  {x.type === "select" ? (
                    <Select value={f[x.key] || undefined} onValueChange={(v) => setF((p) => ({ ...p, [x.key]: v }))}>
                      <SelectTrigger className="rounded-sm" data-testid={`custom-input-${x.key}`}><SelectValue placeholder={`Select ${x.label}`} /></SelectTrigger>
                      <SelectContent>{(x.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input type={x.type === "number" ? "number" : x.type === "date" ? "date" : "text"} className="rounded-sm"
                      value={f[x.key] ?? ""} onChange={(e) => setF((p) => ({ ...p, [x.key]: e.target.value }))} data-testid={`custom-input-${x.key}`} />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button className="rounded-sm" onClick={save} data-testid="custom-fields-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

